import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Factory, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api';
import { PopupField, PopupGridField, PopupCoordinates } from './PopupField';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API_SOURCE_PATH } from './constants';
import ViewOnGoogleMaps from './ViewOnGoogleMaps';

export interface PlantPopupProps {
	longitude: number;
	latitude: number;
	properties: Record<string, string | number>;
	onClose: () => void;
}

interface PlantData {
	id: number;
	shortName: string;
	plantName: string;
	nameplateCapacity: number;
	city: string;
	state: string;
	primaryFuel: string;
	primeMover: string;
	iso: string;
	nerc: string;
	zip: string;
	createdAt: string;
	createdBy: string;
	updatedAt: string;
	updatedBy: string;
	active: string;
	latitude: number;
	longitude: number;
}

const fetchPlantData = async (plantId: string | number): Promise<PlantData> => {
	const response = await apiClient.get(`${API_SOURCE_PATH}/plants/${plantId}`);
	return response.data;
};

export function PlantPopup({ longitude, latitude, properties, onClose }: PlantPopupProps) {
	const {
		data: fetchedData,
		isLoading,
		error
	} = useQuery({
		queryKey: ['geomap', 'plant', properties.id],
		queryFn: () => fetchPlantData(properties.id),
		enabled: !!properties.id
	});

	const plantData: PlantData = {
		id: Number(properties.id) || 0,
		shortName: String(properties.shortName) || '',
		plantName: String(properties.plantName) || '',
		nameplateCapacity: Number(properties.nameplateCapacity) || 0,
		city: String(properties.city) || '',
		state: String(properties.state) || '',
		primaryFuel: String(properties.primaryFuel) || '',
		primeMover: String(properties.primeMover) || '',
		iso: String(properties.iso) || '',
		nerc: String(properties.nerc) || '',
		zip: String(properties.zip) || '',
		active: String(properties.active )|| 'N',
		latitude: latitude,
		longitude: longitude,
		createdAt: '',
		createdBy: '',
		updatedAt: '',
		updatedBy: '',
		...(fetchedData || {})
	};

	const displayName = plantData.shortName || plantData.plantName || `Plant ${plantData.id}`;
	const isActive = plantData.active === 'Y';
	const formattedCapacity = plantData.nameplateCapacity
		? `${plantData.nameplateCapacity.toLocaleString()} MW`
		: 'N/A';

	return (
		<Card className="bg-white dark:bg-slate-900 border shadow-lg min-w-[260px] max-w-[350px]">
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-sm">
						<Factory className="h-4 w-4" />
						Plant Details
					</CardTitle>
					<Button aria-label="close" variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
						<X className="h-3 w-3" />
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<PopupCoordinates longitude={longitude} latitude={latitude} />
				<div className="grid grid-cols-2 gap-2 text-xs">
					<PopupGridField label="ID" value={plantData.id.toString()} />
					<PopupGridField
						label="Status"
						value={
							<Badge variant={isActive ? 'default' : 'secondary'} className="text-xs">
								{isActive ? 'Active' : 'Inactive'}
							</Badge>
						}
					/>
				</div>
				<PopupField label="Name" value={displayName} />
				<PopupField label="Full Name" value={plantData.plantName} />
				<PopupField label="Location" value={`${plantData.city}, ${plantData.state} ${plantData.zip}`} />
				<PopupField label="Capacity" value={formattedCapacity} />
				<PopupField label="Primary Fuel" value={plantData.primaryFuel} />
				<PopupField label="Prime Mover" value={plantData.primeMover} />
				<ViewOnGoogleMaps latitude={latitude} longitude={longitude} />
				{isLoading && (
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				)}
				{error && (
					<div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
						<AlertCircle className="h-3 w-3" />
						Failed to load additional data
					</div>
				)}
				{fetchedData && (
					<div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
						<PopupField label="ISO" value={plantData.iso} />
						<PopupField label="NERC Region" value={plantData.nerc} />
						<PopupField label="Created" value={new Date(plantData.createdAt).toLocaleDateString()} />
						<PopupField label="Updated" value={new Date(plantData.updatedAt).toLocaleDateString()} />
					</div>
				)}
			</CardContent>
		</Card>
	);
}
